import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { SearchableSelect, type SearchableSelectOption } from "../../components/ui/searchable-select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Search,
  Building,
  User,
  Phone,
  Mail,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  Award,
  CheckCircle,
  ChevronDown,
  Loader2,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { getJobs } from "../../services/jobsapi";
import { getAllUsers, getMySubordinates } from "../../services/usersapi";
import { updateTenureStatus, updateVerificationStatus, getJoinedApplications, IPayoutDetails } from "../../services/candidateapi";
import { useRbac } from "../../context";
import { datePickerStyles } from "../../components/constants/styles";
import { getStatusBadgeColor } from "../../components/utils/application.utils";
import ExcelDownloadButton from "../../components/exceldownloadforjoinedcandidates";
import type { Application, PaginationState, UserType } from "../../components/types/application.types";
import { cn } from "../../components/lib/utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { exportJoinedDashboardApplicationsExcel } from "../../services/apiclient";

interface JobDetails {
  id: string;
  title: string;
  companyName: string;
  salary?: string;
  commissionPayable?: string;
  commissionReceived?: string;
}

interface ApplicationWithTenure extends Application {
  tenureDaysLeft?: number;
  tenureStatus?: boolean;
  verificationStatus?: string;
  employeeId?: string;
  job: JobDetails;
  isClaimed?: boolean;
  joiningDate?: string;
  ledBy?: { id: string; username: string };
  tenure?: { status: string; daysLeft?: number | null; isApproved?: boolean };
  tenureInDays?: number;
  commissionPayable?: number;
  commissionReceived?: number;
  position?: string;
  companyLocation?: string;
  email?: string;
  candidateName?: string;
  phone?: string;
  lastWorkingDay?: string;
}

const verificationStatusOptions = [
  { value: "all", label: "All Verification Status" },
  { value: "Verified", label: "Verified" },
  { value: "Not Verified", label: "Not Verified" },
  { value: "Unknown", label: "Unknown" },
];

const tenureStatusOptions = [
  { value: "all", label: "All Tenure Status" },
  { value: "Pending", label: "Pending" },
  { value: "Completed", label: "Completed" },
];

const tenureEligibilityStatusOptions = [
  { value: "all", label: "All Tenure Eligibility" },
  { value: "ELIGIBLE", label: "Eligible" },
  { value: "NOT_ELIGIBLE", label: "Not Eligible" },
  { value: "UNKNOWN", label: "Unknown" },
];

const claimStatusOptions = [
  { value: "all", label: "All Claim Status" },
  { value: "true", label: "Claimed" },
  { value: "false", label: "Not Claimed" },
];

const approveStatusOptions = [
  { value: "all", label: "All Approve Status" },
  { value: "true", label: "Approved" },
  { value: "false", label: "Not Approved" },
];

const JoinedCandidates = () => {
  const navigate = useNavigate();
  const { user } = useRbac();
  const isAdmin = user?.role === "ADMIN";

  const [applications, setApplications] = useState<ApplicationWithTenure[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    limit: 10,
    totalPages: 1,
    totalResults: 0,
  });
  const [jobs, setJobs] = useState<JobDetails[]>([]);
  const [recruiters, setRecruiters] = useState<UserType[]>([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [isLoadingRecruiters, setIsLoadingRecruiters] = useState(false);
  const [searchDebounceTimer, setSearchDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [searchInputValue, setSearchInputValue] = useState<string>("");
  const [selectedCompany, setSelectedCompany] = useState<string>("all");
  const [selectedRecruiter, setSelectedRecruiter] = useState<string>("all");
  const [selectedDateFrom, setSelectedDateFrom] = useState<string>("");
  const [selectedDateTo, setSelectedDateTo] = useState<string>("");
  const [isExportingExcel, setIsExportingExcel] = useState<boolean>(false);
  const [selectedTenureStatus, setSelectedTenureStatus] = useState<string>("all");
  const [selectedTenureEligibilityStatus, setSelectedTenureEligibilityStatus] = useState<string>("all");
  const [selectedClaimStatus, setSelectedClaimStatus] = useState<string>("all");
  const [selectedApproveStatus, setSelectedApproveStatus] = useState<string>("all");
  const [selectedApplicationForTenure, setSelectedApplicationForTenure] = useState<string | null>(null);
  const [showTenureApprovalDialog, setShowTenureApprovalDialog] = useState(false);
  const [selectedApplicationForNotApproved, setSelectedApplicationForNotApproved] = useState<string | null>(null);
  const [showNotApprovedDialog, setShowNotApprovedDialog] = useState(false);
  const [lastWorkingDay, setLastWorkingDay] = useState<string>("");
  const [selectedVerificationStatus, setSelectedVerificationStatus] = useState<string>("all");
  const [payoutDetails, setPayoutDetails] = useState<IPayoutDetails>({
    employeeIdAssignedByCompany: "",
    finalPositionName: "",
    finalSalary: undefined,
    commissionReceivableFromCompany: undefined,
    commissionPayableToSourcer: undefined,

  });
  const isFetchingRef = useRef(false);

  useEffect(() => {
    if (typeof document !== "undefined" && datePickerStyles) {
      const styleSheet = document.createElement("style");
      styleSheet.innerText = datePickerStyles;
      document.head.appendChild(styleSheet);
      return () => {
        if (document.head.contains(styleSheet)) {
          document.head.removeChild(styleSheet);
        }
      };
    }
  }, []);
useEffect(() => {
    if (!showTenureApprovalDialog && !showNotApprovedDialog) {
      setTimeout(() => {
        document.body.style.removeProperty("pointer-events");
        document.body.removeAttribute("data-scroll-locked");
      }, 0);
    }
  }, [showTenureApprovalDialog, showNotApprovedDialog]);
  const companyOptions: SearchableSelectOption[] = useMemo(() => {
    const options: SearchableSelectOption[] = [{ value: "all", label: "All Companies" }];
    const uniqueCompanyNames = Array.from(
      new Set(
        jobs
          .map((job) => (job.companyName || "").trim())
          .filter((name) => Boolean(name) && name !== "N/A")
      )
    ).sort((a, b) => a.localeCompare(b));

    if (uniqueCompanyNames.length > 0) {
      uniqueCompanyNames.forEach((companyName) => {
        options.push({ value: companyName, label: companyName });
      });
    } else if (!isLoadingJobs) {
      options.push({ value: "no-companies", label: "No companies available", disabled: true });
    }
    return options;
  }, [jobs, isLoadingJobs]);

  const recruiterOptions: SearchableSelectOption[] = useMemo(() => {
    const options: SearchableSelectOption[] = [{ value: "all", label: "All Recruiters" }];
    if (recruiters.length > 0) {
      recruiters.forEach((recruiter) => {
        const username = recruiter.username;
        options.push({
          value: recruiter.id,
          label: (
            <div className="text-left">
              <div>{username} <span className="text-sm text-gray-500">{recruiter.role}</span></div>
            </div>
          ),
          searchText: `${username} ${recruiter.email}`.toLowerCase(),
        });
      });
    } else if (!isLoadingRecruiters) {
      options.push({ value: "no-recruiters", label: "No recruiters available", disabled: true });
    }
    return options;
  }, [recruiters, isLoadingRecruiters]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInputValue(value);
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
      }
      const newTimer = setTimeout(() => {
        setSearchTerm(value);
        setPagination((prev) => ({ ...prev, page: 1 }));
      }, 500);
      setSearchDebounceTimer(newTimer);
    },
    [searchDebounceTimer]
  );

  useEffect(() => {
    return () => {
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
      }
    };
  }, [searchDebounceTimer]);

  const fetchCandidates = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const params: any = {
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm || undefined,
        companyName: selectedCompany !== "all" ? selectedCompany : undefined,
        recruiterId: selectedRecruiter !== "all" ? selectedRecruiter : undefined,
        verificationStatus: selectedVerificationStatus !== "all" ? selectedVerificationStatus : undefined,
        tenureStatus: selectedTenureStatus !== "all" ? selectedTenureStatus : undefined,
        tenureEligibilityStatus: selectedTenureEligibilityStatus !== "all" ? selectedTenureEligibilityStatus : undefined,
        claimStatus: selectedClaimStatus !== "all" ? selectedClaimStatus : undefined,
        approveStatus: selectedApproveStatus !== "all" ? selectedApproveStatus : undefined,
        joiningDateFrom: selectedDateFrom || undefined,
        joiningDateTo: selectedDateTo || undefined,
        _t: Date.now(),
      };

      const cleanParams = Object.fromEntries(
        Object.entries(params).filter(([_, value]) => value !== undefined)
      );

      const response = await getJoinedApplications(params);
      const appsData = response?.data?.data?.applications || response?.data?.applications || [];

      let apps = appsData.map((app: any) => {
        const joiningDate = app.joiningDate ? new Date(app.joiningDate) : null;
        const tenureInDays = app.tenureInDays || 0;
        let daysLeft = 0;

        if (joiningDate && !isNaN(joiningDate.getTime())) {
          const currentDate = new Date();
          const timeDiff = tenureInDays - Math.floor((currentDate.getTime() - joiningDate.getTime()) / (1000 * 3600 * 24));
          daysLeft = Math.max(0, timeDiff);
        }

        const tenureStatus = app.tenure?.status || "Pending";

        return {
          applicationId: app.applicationId || app._id || "",
          candidate: {
            id: app._id || app.applicationId || "",
            name: app.candidateName || "N/A",
            phone: app.phone || "N/A",
            email: app.email || "N/A",
            currentLocationDetails: {
              city: app.companyLocation || app.candidate?.currentLocationDetails?.city || "N/A",
              fullAddress: app.companyLocation || app.candidate?.currentLocationDetails?.fullAddress || "N/A",
            },
          },
          job: {
            id: app.jobId || "",
            title: app.position || app.job?.title || "N/A",
            companyName: app.companyName || app.job?.companyName || "N/A",
            salary: app.salary || app.job?.salary || "N/A",
            commissionPayable: app.commissionPayable || app.job?.commissionPayable || 0,
            commissionReceived: app.commissionReceived || app.job?.commissionReceived || 0,
          },
          employeeId: app.employeeId || "N/A",
          joiningDate: app.joiningDate || "",
          ledBy: app.ledBy || { id: "", username: "N/A" },
          tenure: {
            status: tenureStatus,
            daysLeft: daysLeft,
            isApproved: app.tenure?.isApproved || false,
          },
          verificationStatus: app.verificationStatus || "Unknown",
          isClaimed: app.isCommissionClaimed || false,
          currentStatus: {
            status: app.status || "Unknown",
          },
          commissionPayable: app.commissionPayable || 0,
          commissionReceived: app.commissionReceived || 0,
          position: app.position || app.job?.title || "N/A",
          companyLocation: app.companyLocation || "N/A",
          lastWorkingDay: app.lastWorkingDay || "",
          appliedOn: new Date(),
          sourcedBy: "Sourcer",
        };
      });

      if (selectedVerificationStatus !== "all") {
        apps = apps.filter(
          (app: ApplicationWithTenure) => (app.verificationStatus || "Unknown") === selectedVerificationStatus
        );
      }

      setApplications(apps);
      setPagination((prev) => ({
        ...prev,
        totalPages: response.data?.data?.totalPages || response.data?.totalPages || 1,
        totalResults: response.data?.data?.totalResults || response.data?.totalResults || apps.length,
      }));
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Failed to fetch candidates. Please try again.";
      setError(errorMessage);
      setApplications([]);
      toast.error(errorMessage, { duration: 4000 });
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [
    pagination.page,
    pagination.limit,
    searchTerm,
    selectedCompany,
    selectedRecruiter,
    selectedDateFrom,
    selectedDateTo,
    selectedTenureStatus,
    selectedTenureEligibilityStatus,
    selectedClaimStatus,
    selectedApproveStatus,
    selectedVerificationStatus,
  ]);

  useEffect(() => {
    fetchCandidates();
  }, [
    searchTerm,
    selectedCompany,
    selectedRecruiter,
    selectedDateFrom,
    selectedDateTo,
    selectedTenureStatus,
    selectedTenureEligibilityStatus,
    selectedClaimStatus,
    selectedApproveStatus,
    selectedVerificationStatus,
    pagination.page,
    pagination.limit,
  ]);

  useEffect(() => {
    fetchJobs();
    fetchRecruiters();
  }, []);

  const fetchJobs = useCallback(async (value?: string) => {
    setIsLoadingJobs(true);
    try {
      const response = await getJobs({ page: 1, limit: 100, search: value?.trim() || undefined, _t: Date.now() });
      const jobsData = response?.data?.jobs || [];
      if (jobsData.length > 0) {
        setJobs(
          jobsData.map((job: any) => ({
            id: job.id || job._id || "",
            title: job.title || "N/A",
            companyName: job.companyName || "N/A",
            salary: job.salary || "N/A",
            commissionPayable: job.commissionPayable || 0,
            commissionReceived: job.commissionReceived || 0,
          }))
        );
      } else {
        setJobs([]);
        // toast.error("No jobs found in the system.", { duration: 3000 });
      }
    } catch (err: any) {
      console.error("Failed to fetch jobs:", err);
      setJobs([]);
      toast.error("Failed to load jobs for filtering", { duration: 3000 });
    } finally {
      setIsLoadingJobs(false);
    }
  }, []);

  const fetchRecruiters = useCallback(async (value?: string) => {
    setIsLoadingRecruiters(true);
    try {
      let response = user.role === "ADMIN" ? await getAllUsers({ role: "FBDE", search: value }) : await getMySubordinates();
      if (user.role === "ADMIN") {
        const response2 = await getAllUsers({ role: "CONSULTANT", search: value });
        response.data.users = response.data.users.concat(response2.data.users);
      }
      if (user.role === "ADMIN") {
        const response2 = await getAllUsers({ role: "SHM", search: value });
        response.data.users = response.data.users.concat(response2.data.users);
      }
      const recruitersData = response?.data?.subordinates || response?.data?.users || [];
      if (recruitersData.length > 0) {
        setRecruiters(recruitersData);
      } else {
        setRecruiters([]);
        // toast.error("No recruiters found in the system.", { duration: 3000 });
      }
    } catch (err: any) {
      console.error("Failed to fetch recruiters:", err);
      setRecruiters([]);
      toast.error("Failed to load recruiters for filtering", { duration: 3000 });
    } finally {
      setIsLoadingRecruiters(false);
    }
  }, []);

  const handleTenureStatusChange = async (applicationId: string, status: string) => {
    try {
      const selectedApp = applications.find((app) => app.applicationId === applicationId);
      if (status === "Approved") {
        setSelectedApplicationForTenure(applicationId);
        setPayoutDetails({
          employeeIdAssignedByCompany: "",
          finalPositionName: selectedApp?.position || "",
          finalSalary: undefined,
          commissionReceivableFromCompany: undefined,
          commissionPayableToSourcer: undefined,
         
        });
        setShowTenureApprovalDialog(true);
      } else if (status === "Pending") {
        toast.loading("Updating tenure status...", { id: "tenure-update" });
        await updateTenureStatus(
          applicationId,
          {
            isTenureApproved: null,
            comments: "Tenure decision reset to pending",
          },
          { silent: true }
        );
        setApplications((prevApps) =>
          prevApps.map((app) =>
            app.applicationId === applicationId
              ? {
                  ...app,
                  tenure: {
                    ...app.tenure!,
                    status: "Pending",
                    isApproved: undefined,
                  },
                }
              : app
          )
        );
        toast.success("Tenure status updated to Pending", { id: "tenure-update" });
        await fetchCandidates();
      } else if (status === "Not Approved") {
        if (selectedApp?.lastWorkingDay) {
          toast.loading("Updating tenure status...", { id: "tenure-update" });
          await updateTenureStatus(applicationId, {
            isTenureApproved: false,
            comments: "Tenure rejected",
            lastWorkingDay: selectedApp.lastWorkingDay,
            payoutDetails: {
              employeeIdAssignedByCompany: undefined,
              finalPositionName: undefined,
              finalSalary: undefined,
              commissionReceivableFromCompany: undefined,
              commissionPayableToSourcer: undefined,
            },
          });
          setApplications((prevApps) =>
            prevApps.map((app) =>
              app.applicationId === applicationId
                ? {
                    ...app,
                    tenure: {
                      ...app.tenure!,
                      status: "Not Approved",
                      isApproved: false,
                    },
                  }
                : app
            )
          );
          toast.success("Tenure status updated to Not Approved", { id: "tenure-update" });
          await fetchCandidates();
          return;
        }
        setSelectedApplicationForNotApproved(applicationId);
        setLastWorkingDay("");
        setShowNotApprovedDialog(true);
      }
    } catch (error) {
      console.error("Failed to update tenure status:", error);
      toast.error("Failed to update tenure status", { id: "tenure-update" });
    }
  };

  const handleNotApprovedStatusUpdate = async (payload: { skip: boolean }) => {
    if (!selectedApplicationForNotApproved) return;
    try {
      toast.loading("Updating tenure status...", { id: "tenure-update" });
      await updateTenureStatus(selectedApplicationForNotApproved, {
        isTenureApproved: false,
        comments: "Tenure rejected",
        lastWorkingDay: payload.skip ? undefined : lastWorkingDay,
        payoutDetails: {
          employeeIdAssignedByCompany: undefined,
          finalPositionName: undefined,
          finalSalary: undefined,
          commissionReceivableFromCompany: undefined,
          commissionPayableToSourcer: undefined,
        },
      });
      setApplications((prevApps) =>
        prevApps.map((app) =>
          app.applicationId === selectedApplicationForNotApproved
            ? {
                ...app,
                tenure: {
                  ...app.tenure!,
                  status: "Not Approved",
                  isApproved: false,
                },
              }
            : app
        )
      );
      toast.success("Tenure status updated to Not Approved", { id: "tenure-update" });
      setShowNotApprovedDialog(false);
      setSelectedApplicationForNotApproved(null);
      setLastWorkingDay("");
      await fetchCandidates();
    } catch (error) {
      console.error("Failed to update tenure status:", error);
      toast.error("Failed to update tenure status", { id: "tenure-update" });
    }
  };

  const handleApproveTenure = async () => {
    if (!selectedApplicationForTenure) return;
    try {
      toast.loading("Approving tenure status...", { id: "tenure-approve" });
      await updateTenureStatus(selectedApplicationForTenure, {
        isTenureApproved: true,
        comments: "Tenure approved",
        payoutDetails: {
          employeeIdAssignedByCompany: payoutDetails.employeeIdAssignedByCompany || undefined,
          finalPositionName: payoutDetails.finalPositionName || undefined,
          finalSalary: payoutDetails.finalSalary,
          commissionReceivableFromCompany: payoutDetails.commissionReceivableFromCompany,
          commissionPayableToSourcer: payoutDetails.commissionPayableToSourcer,

        },
      });
      setApplications((prevApps) =>
        prevApps.map((app) =>
          app.applicationId === selectedApplicationForTenure
            ? {
                ...app,
                tenure: {
                  ...app.tenure!,
                  status: "Approved",
                  isApproved: true,
                },
              }
            : app
        )
      );
      toast.success("Tenure status updated to Approved", { id: "tenure-approve" });
      setShowTenureApprovalDialog(false);
      setSelectedApplicationForTenure(null);
      setPayoutDetails({
        employeeIdAssignedByCompany: "",
        finalPositionName: "",
        finalSalary: undefined,
        commissionReceivableFromCompany: undefined,
        commissionPayableToSourcer: undefined,

      });
      await fetchCandidates();
    } catch (error) {
      console.error("Failed to approve tenure status:", error);
      toast.error("Failed to approve tenure status", { id: "tenure-approve" });
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page: newPage }));
    }
  };

  const handleVerificationStatusChange = async (applicationId: string, status: string) => {
    try {
      toast.loading("Updating verification status...", { id: "verification-update" });
      await updateVerificationStatus(applicationId, {
        verificationStatus: status,
        comments: `Verification status updated to ${status}`,
      });
      setApplications((prevApps) =>
        prevApps.map((app) =>
          app.applicationId === applicationId ? { ...app, verificationStatus: status } : app
        )
      );
      toast.success(`Verification status updated to ${status}`, { id: "verification-update" });
      await fetchCandidates();
    } catch (error) {
      console.error("Failed to update verification status:", error);
      toast.error("Failed to update verification status", { id: "verification-update" });
    }
  };

  const getAppointmentsForExcel = async () => {
    try {
      let allApplications: any[] = [];
      let currentPage = 1;
      let totalPages = 1;

      while (currentPage <= totalPages) {
        const params: any = {
          page: currentPage,
          limit: 100,
          search: searchTerm || undefined,
          companyName: selectedCompany !== "all" ? selectedCompany : undefined,
          recruiterId: selectedRecruiter !== "all" ? selectedRecruiter : undefined,
          tenureStatus: selectedTenureStatus !== "all" ? selectedTenureStatus : undefined,
          tenureEligibilityStatus: selectedTenureEligibilityStatus !== "all" ? selectedTenureEligibilityStatus : undefined,
          claimStatus: selectedClaimStatus !== "all" ? selectedClaimStatus : undefined,
          approveStatus: selectedApproveStatus !== "all" ? selectedApproveStatus : undefined,
          joiningDateFrom: selectedDateFrom || undefined,
          joiningDateTo: selectedDateTo || undefined,
          _t: Date.now(),
        };

        const response = await getJoinedApplications(params);

        const appsData = response?.data?.data?.applications || response?.data?.applications || [];
        if (!appsData.length) {
          break;
        }

        const apps = appsData.map((app: any) => ({
          "Joining Date": app.joiningDate && !isNaN(new Date(app.joiningDate).getTime())
            ? new Date(app.joiningDate).toLocaleDateString("en-GB")
            : "N/A",
          "Candidate Name": app.candidateName || "N/A",
          "Email": app.email || "N/A",
          "Company Name": app.companyName || app.job?.companyName || "N/A",
          "Company Location": app.companyLocation || app.candidate?.currentLocationDetails?.city || "N/A",
          "Employee ID": app.employeeId || "N/A",
          "Applied By": app.ledBy?.username || "N/A",
          "Position": app.position || app.job?.title || "N/A",
          "Phone": app.phone || "N/A",
          "Salary": app.salary || app.job?.salary || "N/A",
          "Commission Received": app.commissionReceived || app.job?.commissionReceived || "N/A",
          "Commission Payable": app.commissionPayable || app.job?.commissionPayable || "N/A",
          "Commission Claimed": app.isCommissionClaimed ? "Yes" : "No",
          "Tenure Status": app.tenure?.status || "Pending",
          "Tenure Days Left": app.tenure?.daysLeft ?? "N/A",
          "Verification Status": app.verificationStatus || "Unknown",
          "City": app.candidate?.currentLocationDetails?.city || app.companyLocation || "N/A",
        }));

        allApplications = [...allApplications, ...apps];
        totalPages = response.data?.data?.totalPages || response.data?.totalPages || 1;
        currentPage += 1;
      }

      return allApplications;
    } catch (err: any) {
      console.error("Failed to fetch applications for Excel:", err);
      toast.error(err.response?.data?.message || "Failed to fetch applications for export");
      return [];
    }
  };

  const SortableHeader: React.FC<{ column: string; children: React.ReactNode; className?: string }> = ({
    column,
    children,
    className,
  }) => (
    <th
      className={cn(
        "px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors sticky top-0 bg-[#FFF5F2] z-10",
        className
      )}
    >
      <div className="flex items-center gap-1">{children}</div>
    </th>
  );

  const debounce = useCallback((fn: (...args: any[]) => void, delay: number) => {
    let timer: NodeJS.Timeout | null = null;
    return (...args: any[]) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        fn(...args);
        timer = null;
      }, delay);
    };
  }, []);

  const debouncedRecruiterSearch = useMemo(
    () => debounce((value: string) => {
      fetchRecruiters(value);
    }, 300),
    [debounce]
  );

  const debouncedCompanySearch = useMemo(
    () =>
      debounce((value: string) => {
        fetchJobs(value);
      }, 300),
    [debounce, fetchJobs]
  );

  const canUpdateTenureStatus = (app: ApplicationWithTenure) => {
    const tenureStatus = app.tenure?.status || "Pending";
    const daysLeft = app.tenure?.daysLeft ?? 1;
    const verificationStatus = app.verificationStatus || "Unknown";
    return daysLeft <= 0 && verificationStatus !== "Unknown" && tenureStatus === "Pending";
  };

  const handleExcelDownload = async () => {
    setIsExportingExcel(true);
    try {
      await exportJoinedDashboardApplicationsExcel({
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm || undefined,
        companyName: selectedCompany !== "all" ? selectedCompany : undefined,
        recruiterId: selectedRecruiter !== "all" ? selectedRecruiter : undefined,
        tenureStatus: selectedTenureStatus !== "all" ? selectedTenureStatus : undefined,
        tenureEligibilityStatus: selectedTenureEligibilityStatus !== "all" ? selectedTenureEligibilityStatus : undefined,
        claimStatus: selectedClaimStatus !== "all" ? selectedClaimStatus : undefined,
        approveStatus: selectedApproveStatus !== "all" ? selectedApproveStatus : undefined,
        joiningDateFrom: selectedDateFrom || undefined,
        joiningDateTo: selectedDateTo || undefined,
      });
    } finally {
      setIsExportingExcel(false);
    }
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-3 sm:px-6 py-4">
        <div className="flex items-start gap-3 mb-4">
          <Button variant="ghost" size="sm" className="p-1 sm:p-2 flex-shrink-0" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">Joined Candidates</h1>
                <p className="text-xs sm:text-sm text-gray-500">View and manage candidates with Joined status</p>
              </div>
            </div>
        
        {/* Search and Download Row */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search Candidates..."
                  value={searchInputValue}
                  onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-10 w-full border-gray-300"
                />
              </div>
              <Button
                variant="outline"
                onClick={handleExcelDownload}
                disabled={isExportingExcel}
            className="flex items-center justify-center gap-2 rounded-lg border-orange-300 text-orange-600 hover:bg-orange-50 px-4 py-2"
              >
                {isExportingExcel ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="hidden sm:inline">Downloading...</span>
                    <span className="sm:hidden">Loading...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                    <span className="hidden sm:inline">Download Excel</span>
                    <span className="sm:hidden">Excel</span>
                  </>
                )}
              </Button>
            </div>
        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
            <SearchableSelect
              options={companyOptions}
              value={selectedCompany}
              onValueChange={(v) => {
                setSelectedCompany(v);
                setPagination((p) => ({ ...p, page: 1 }));
              }}
              onSearchChange={debouncedCompanySearch}
              placeholder={isLoadingJobs ? "Loading companies..." : "All Companies"}
              emptyText="No companies found"
              searchPlaceholder="Search companies..."
            className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
              icon={<Building className="h-4 w-4 text-gray-500" />}
              searchable
            />
            <SearchableSelect
              options={recruiterOptions}
              value={selectedRecruiter}
              onValueChange={(v) => {
                setSelectedRecruiter(v);
                setPagination((p) => ({ ...p, page: 1 }));
              }}
              onSearchChange={debouncedRecruiterSearch}
              placeholder={isLoadingRecruiters ? "Loading recruiters..." : "All Recruiters"}
              searchPlaceholder="Search recruiters..."
              icon={<User className="h-4 w-4 text-gray-500" />}
              emptyText="No recruiters available"
            className="w-full rounded-lg border-gray-200 text-black focus:ring-2 focus:ring-orange-500 h-10"
              loading={isLoadingRecruiters}
            />
            <Select
              value={selectedTenureStatus}
              onValueChange={(v) => {
                setSelectedTenureStatus(v);
                setPagination((p) => ({ ...p, page: 1 }));
              }}
            >
            <SelectTrigger className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500">
                <div className="flex items-center gap-2">
                  <Award className="h-4 w-4 text-gray-500" />
                  <SelectValue placeholder="Tenure Status" />
                </div>
              </SelectTrigger>
              <SelectContent>
                {tenureStatusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={selectedDateFrom}
              onChange={(e) => {
                setSelectedDateFrom(e.target.value);
                setPagination((p) => ({ ...p, page: 1 }));
              }}
            className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
              title="From Date"
            />
            <Input
              type="date"
              value={selectedDateTo}
              onChange={(e) => {
                setSelectedDateTo(e.target.value);
                setPagination((p) => ({ ...p, page: 1 }));
              }}
            className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
              title="To Date"
            />
            <Select
              value={selectedVerificationStatus}
              onValueChange={(v) => {
                setSelectedVerificationStatus(v);
                setPagination((p) => ({ ...p, page: 1 }));
              }}
            >
            <SelectTrigger className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-gray-500" />
                  <SelectValue placeholder="Verification Status" />
                </div>
              </SelectTrigger>
              <SelectContent>
                {verificationStatusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto bg-background">
        {/* Desktop Table View */}
        <div className="hidden lg:block min-w-full">
          <table className="min-w-full divide-y divide-border">
            <thead style={{ backgroundColor: "#FFF5F2" }}>
              <tr>
                <th className="px-4 py-3 w-12 sticky top-0 bg-[#FFF5F2] z-10"></th>
                <SortableHeader column="Candidate Name" className="min-w-48 sticky left-0 bg-[#FFF5F2] z-20 border-r border-border">
                  Candidate Name
                </SortableHeader>
                <SortableHeader column="Company Name" className="bg-[#FFF5F2] min-w-32">Company Name</SortableHeader>
                <SortableHeader column="Location" className="bg-[#FFF5F2] min-w-32">Location</SortableHeader>
                <SortableHeader column="Phone" className="bg-[#FFF5F2] min-w-32">Phone</SortableHeader>
                <SortableHeader column="Email" className="bg-[#FFF5F2] min-w-64">Email</SortableHeader>
                <SortableHeader column="Joined Date" className="bg-[#FFF5F2] min-w-32">Joined Date</SortableHeader>
                <SortableHeader column="Sourced By" className="bg-[#FFF5F2] min-w-32">Sourced By</SortableHeader>
                <SortableHeader column="Employee Id" className="bg-[#FFF5F2] min-w-32">Employee Id</SortableHeader>
                <SortableHeader column="Last Working Day" className="bg-[#FFF5F2] min-w-32">Last Working Day</SortableHeader>
                <SortableHeader column="Tenure Days Left" className="bg-[#FFF5F2] min-w-32">Tenure Days</SortableHeader>
                <SortableHeader column="Tenure Status" className="bg-[#FFF5F2] min-w-32">Tenure Status</SortableHeader>
                <SortableHeader column="Verification Status" className="bg-[#FFF5F2] min-w-32">Verification Status</SortableHeader>
              </tr>
            </thead>
            <tbody className="bg-background divide-y divide-border">
              {isLoading ? (
                <>
                  {[...Array(10)].map((_, index) => (
                    <tr key={`shimmer-${index}`} className="bg-all divide-y divide-border">
                      <td className="px-4 py-4"></td>
                      <td className="px-4 py-4 sticky left-0 bg-white z-10 border-r border-border">
                        <div className="animate-pulse bg-gray-200 rounded w-32 h-4"></div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-40 h-4"></div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-32 h-4"></div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-24 h-4"></div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-48 h-4"></div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-28 h-4"></div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-24 h-4"></div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-20 h-4"></div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-24 h-4"></div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-20 h-4"></div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded-full w-24 h-6"></div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded-full w-28 h-6"></div>
                      </td>
                    </tr>
                  ))}
                </>
              ) : error ? (
                <tr>
                  <td colSpan={isAdmin ? 12 : 11} className="px-4 py-12 text-center">
                    <p className="text-muted-foreground">{error}</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={fetchCandidates}
                      className="mt-4"
                    >
                      Retry
                    </Button>
                  </td>
                </tr>
              ) : applications.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 12 : 11} className="px-4 py-12 text-center">
                    <p className="text-muted-foreground">No joined candidates found matching your criteria.</p>
                  </td>
                </tr>
              ) : (
                applications.map((app) => (
                  <tr key={app.applicationId} className={cn("hover:bg-muted/30 transition-colors cursor-pointer")}>
                    <td className="px-4 py-4"></td>
                    <td className="px-4 py-4 sticky left-0 bg-white z-10 border-r border-border group">
                      <div className="flex items-center gap-3">
                        <span className="font-medium transition-colors cursor-pointer text-foreground hover:text-primary">
                          {app.candidate.name}
                        </span>
                      </div>
                      <div className="absolute top-0 left-[calc(100%+0.5rem)] mt-1 w-80 p-4 bg-white shadow-lg rounded border border-border opacity-0 pointer-events-none transition-opacity group-hover:opacity-100 group-hover:pointer-events-auto z-30">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold text-lg">{app.candidate.name}</h4>
                          <Badge variant="secondary" className="text-xs">
                            {app.currentStatus?.status || "N/A"}
                          </Badge>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Job:</span>
                            <span className="font-medium">{app.job?.title || "N/A"}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Location:</span>
                            <div className="max-w-48 truncate" title={app.candidate.currentLocationDetails?.fullAddress}>
                              {app.candidate.currentLocationDetails?.fullAddress || "N/A"}
                            </div>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Phone:</span>
                            <span className="font-medium">{app.candidate.phone}</span>
                          </div>
                        </div>
                        <div className="pt-2 border-t border-border">
                          <div className="text-xs text-muted-foreground">
                            Joined {app.joiningDate && !isNaN(new Date(app.joiningDate).getTime()) ? new Date(app.joiningDate).toLocaleDateString("en-GB") : "N/A"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-foreground truncate max-w-48">{app.job?.companyName || "N/A"}</td>
                    <td className="px-4 py-4 text-sm text-foreground truncate max-w-48">{app.candidate.currentLocationDetails?.city || "N/A"}</td>
                    <td className="px-4 py-4 text-sm text-foreground">
                      <span
                        className="cursor-pointer hover:text-orange-600"
                        title="Copy phone number"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(app.candidate.phone);
                          toast.success("Phone number copied!");
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
                        onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
                      >
                        {app.candidate.phone}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-foreground">
                      <span
                        className="cursor-pointer hover:text-orange-600"
                        title="Copy email"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(app.candidate.email);
                          toast.success("Email copied!");
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
                        onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
                      >
                        {app.candidate.email}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-muted-foreground">
                      {app.joiningDate && !isNaN(new Date(app.joiningDate).getTime()) ? new Date(app.joiningDate).toLocaleDateString("en-GB") : "N/A"}
                    </td>
                    <td className="px-4 py-4 text-sm text-foreground">{app.ledBy?.username || "N/A"}</td>

                    <td className="px-4 py-4 text-sm text-foreground">{app.employeeId || "N/A"}</td>
                    <td className="px-4 py-4 text-sm text-foreground">
                      {app.lastWorkingDay && !isNaN(new Date(app.lastWorkingDay).getTime())
                        ? new Date(app.lastWorkingDay).toLocaleDateString("en-GB")
                        : "-"}
                    </td>
                    <td className="px-4 py-4 text-sm text-foreground">
                      {app.verificationStatus === "Not Verified" ||
                      (app.verificationStatus === "Verified" && app.tenure?.status === "Not Approved") ? (
                        <Badge className="border font-medium px-3 py-1 bg-red-50 text-red-700">Not Eligible</Badge>
                      ) : (
                        `${app.tenure?.daysLeft ?? "N/A"} days`
                      )}
                    </td>
                    <td className="px-4 py-4">
                      {(() => {
  const tenureStatus = app.tenure?.status || "Pending";
  const daysLeft = app.tenure?.daysLeft ?? 1;
  const verificationStatus = app.verificationStatus || "Unknown";

  // if (tenureStatus === "Approved" || tenureStatus === "Completed") {
  //   return (
  //     <Badge className="border font-medium px-3 py-1 bg-green-50 text-green-700">
  //       {tenureStatus}
  //     </Badge>
  //   );
  // } else if (tenureStatus === "Not Approved") {
  //   return (
  //     <Badge className="border font-medium px-3 py-1 bg-red-50 text-red-700">
  //       {tenureStatus}
  //     </Badge>
  //   );
  // } else
     if (tenureStatus === "Pending" || tenureStatus === "Not Approved" || tenureStatus === "Completed" || tenureStatus === "Approved") {
    const canUpdate = daysLeft <= 0 && verificationStatus === "Verified";

    return user.role === "ADMIN" ? (
      verificationStatus === "Unknown" ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                "rounded-lg px-3 py-1.5 font-medium bg-gray-100 text-gray-400 cursor-not-allowed",
              )}
              disabled
              title="Update tenure status"
            >
              Pending <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
        </DropdownMenu>
      ) : canUpdate ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className = {cn("rounded-lg px-3 py-1.5 font-medium", tenureStatus === "Approved" ? "border-green-300 text-green-600 hover:bg-green-50" : tenureStatus === "Not Approved" ? "border-red-300 text-red-600 hover:bg-red-50" : "bg-gray-100 text-gray-400")}
              title={
                !canUpdate
                  ? daysLeft > 0
                    ? "Tenure period not completed"
                    : "Verification status must be Verified"
                  : "Update tenure status"
              }
            >
              {tenureStatus} <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="rounded-lg shadow-lg border-0 bg-white/95 backdrop-blur-sm">
            <DropdownMenuItem
              onClick={() =>
                handleTenureStatusChange(app.applicationId, "Approved")
              }
              className="cursor-pointer hover:bg-green-50 px-3 py-2 text-green-700"
            >
              Approved
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                handleTenureStatusChange(app.applicationId, "Not Approved")
              }
              className="cursor-pointer hover:bg-red-50 px-3 py-2 text-red-700"
            >
              Not Approved
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                handleTenureStatusChange(app.applicationId, "Pending")
              }
              className="cursor-pointer hover:bg-gray-50 px-3 py-2 text-gray-700"
            >
              Pending
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
         <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className = {cn("rounded-lg px-3 py-1.5 font-medium", tenureStatus === "Approved" ? "border-green-300 text-green-600 hover:bg-green-50" : tenureStatus === "Not Approved" ? "border-red-300 text-red-600 hover:bg-red-50" : "bg-gray-100 text-gray-400")}
              title={
                !canUpdate
                  ? daysLeft > 0
                    ? "Tenure period not completed"
                    : "Verification status must be Verified"
                  : "Update tenure status"
              }
            >
              {tenureStatus}<ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="rounded-lg shadow-lg border-0 bg-white/95 backdrop-blur-sm">
            <DropdownMenuItem
              onClick={() =>
                handleTenureStatusChange(app.applicationId, "Pending")
              }
              className="cursor-pointer hover:bg-gray-50 px-3 py-2 text-gray-700"
            >
              Pending
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                handleTenureStatusChange(app.applicationId, "Not Approved")
              }
              className="cursor-pointer hover:bg-red-50 px-3 py-2 text-red-700"
            >
              Not Approved
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    ) : (
      <Badge className="border font-medium px-3 py-1 bg-yellow-50 text-yellow-700">
        {tenureStatus}
      </Badge>
    );
  }
})()}

                    </td>
                    <td className="px-4 py-4">
                      {user.role === "ADMIN" ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className={cn("rounded-lg px-3 py-1.5", app.verificationStatus === "Verified" ? "border-green-300 text-green-600 hover:bg-green-50" : app.verificationStatus === "Not Verified" ? "border-red-300 text-red-600 hover:bg-red-50" :  "border-orange-300 text-orange-600 hover:bg-orange-50" )}
                              style={{ width: "90%" }}
                            >
                              {app.verificationStatus} <ChevronDown className="ml-2 h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="rounded-lg shadow-lg border-0 bg-white/95 backdrop-blur-sm">
                            <DropdownMenuItem
                              onClick={() => handleVerificationStatusChange(app.applicationId, "Verified")}
                              className="cursor-pointer hover:bg-green-50 px-3 py-2 text-green-700"
                            >
                              Verified
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleVerificationStatusChange(app.applicationId, "Not Verified")}
                              className="cursor-pointer hover:bg-red-50 px-3 py-2 text-red-700"
                            >
                              Not Verified
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleVerificationStatusChange(app.applicationId, "Unknown")}
                              className="cursor-pointer hover:bg-gray-50 px-3 py-2 text-gray-700"
                            >
                              Unknown
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <div className="text-sm text-muted-foreground">{app.verificationStatus || "Select Status"}</div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden">
          {isLoading ? (
            <div className="space-y-4 p-4">
              {[...Array(5)].map((_, index) => (
                <div key={`mobile-shimmer-${index}`} className="bg-white rounded-lg border p-4">
                  <div className="animate-pulse space-y-3">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                    <div className="flex gap-2">
                      <div className="h-6 bg-gray-200 rounded w-16"></div>
                      <div className="h-6 bg-gray-200 rounded w-20"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="p-4 text-center">
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchCandidates}
                className="mt-4"
              >
                Retry
              </Button>
            </div>
          ) : applications.length === 0 ? (
            <div className="p-4 text-center">
              <p className="text-muted-foreground">No joined candidates found matching your criteria.</p>
            </div>
          ) : (
            <div className="space-y-4 p-4">
              {applications.map((app) => (
                <div key={app.applicationId} className="bg-white rounded-lg border p-4 space-y-3">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-lg text-gray-900 truncate">{app.candidate.name}</h3>
                      <p className="text-sm text-gray-600 truncate">{app.job?.companyName || "N/A"}</p>
                    </div>
                    <Badge variant="secondary" className="text-xs flex-shrink-0">
                      {app.currentStatus?.status || "N/A"}
                    </Badge>
                  </div>

                  {/* Contact Info */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="h-4 w-4 text-gray-500" />
                      <span
                        className="cursor-pointer hover:text-orange-600"
                        onClick={() => {
                          navigator.clipboard.writeText(app.candidate.phone);
                          toast.success("Phone number copied!");
                        }}
                      >
                        {app.candidate.phone}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="h-4 w-4 text-gray-500" />
                      <span
                        className="cursor-pointer hover:text-orange-600 truncate"
                        onClick={() => {
                          navigator.clipboard.writeText(app.candidate.email);
                          toast.success("Email copied!");
                        }}
                      >
                        {app.candidate.email}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Building className="h-4 w-4 text-gray-500" />
                      <span className="truncate">{app.candidate.currentLocationDetails?.city || "N/A"}</span>
                    </div>
                  </div>

                  {/* Job Details */}
                  <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Position:</span>
                      <span className="font-medium">{app.position || "N/A"}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Joined:</span>
                      <span className="font-medium">
                        {app.joiningDate && !isNaN(new Date(app.joiningDate).getTime()) 
                          ? new Date(app.joiningDate).toLocaleDateString("en-GB") 
                          : "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Sourced By:</span>
                      <span className="font-medium">{app.ledBy?.username || "N/A"}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Employee ID:</span>
                      <span className="font-medium">{app.employeeId || "N/A"}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Last Working Day:</span>
                      <span className="font-medium">
                        {app.lastWorkingDay && !isNaN(new Date(app.lastWorkingDay).getTime())
                          ? new Date(app.lastWorkingDay).toLocaleDateString("en-GB")
                          : "-"}
                      </span>
                    </div>
                  </div>

                  {/* Status Actions */}
                  <div className="flex flex-col gap-3">
                    {/* Tenure Status */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Tenure Status:</span>
                      {(() => {
                        const tenureStatus = app.tenure?.status || "Pending";
                        const daysLeft = app.tenure?.daysLeft ?? 1;
                        const verificationStatus = app.verificationStatus || "Unknown";

                        if (tenureStatus === "Pending" || tenureStatus === "Not Approved" || tenureStatus === "Completed" || tenureStatus === "Approved") {
                          const canUpdate = daysLeft <= 0 && verificationStatus === "Verified";

                          return user.role === "ADMIN" ? (
                            verificationStatus === "Unknown" ? (
                              <Button
                                variant="outline"
                                size="sm"
                                className="rounded-lg px-3 py-1.5 font-medium text-gray-400 bg-gray-100 cursor-not-allowed"
                                disabled
                              >
                                Pending
                              </Button>
                            ) : canUpdate ? (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className={cn("rounded-lg px-3 py-1.5 font-medium", 
                                      tenureStatus === "Approved" ? "border-green-300 text-green-600 hover:bg-green-50" : 
                                      tenureStatus === "Not Approved" ? "border-red-300 text-red-600 hover:bg-red-50" : 
                                      "bg-gray-100 text-gray-400")}
                                  >
                                    {tenureStatus} <ChevronDown className="ml-2 h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="rounded-lg shadow-lg border-0 bg-white/95 backdrop-blur-sm">
                                  <DropdownMenuItem
                                    onClick={() => handleTenureStatusChange(app.applicationId, "Approved")}
                                    className="cursor-pointer hover:bg-green-50 px-3 py-2 text-green-700"
                                  >
                                    Approved
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleTenureStatusChange(app.applicationId, "Not Approved")}
                                    className="cursor-pointer hover:bg-red-50 px-3 py-2 text-red-700"
                                  >
                                    Not Approved
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleTenureStatusChange(app.applicationId, "Pending")}
                                    className="cursor-pointer hover:bg-gray-50 px-3 py-2 text-gray-700"
                                  >
                                    Pending
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            ) : (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className={cn("rounded-lg px-3 py-1.5 font-medium", 
                                      tenureStatus === "Approved" ? "border-green-300 text-green-600 hover:bg-green-50" : 
                                      tenureStatus === "Not Approved" ? "border-red-300 text-red-600 hover:bg-red-50" : 
                                      "bg-gray-100 text-gray-400")}
                                  >
                                    {tenureStatus} <ChevronDown className="ml-2 h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="rounded-lg shadow-lg border-0 bg-white/95 backdrop-blur-sm">
                                  <DropdownMenuItem
                                    onClick={() => handleTenureStatusChange(app.applicationId, "Pending")}
                                    className="cursor-pointer hover:bg-gray-50 px-3 py-2 text-gray-700"
                                  >
                                    Pending
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleTenureStatusChange(app.applicationId, "Not Approved")}
                                    className="cursor-pointer hover:bg-red-50 px-3 py-2 text-red-700"
                                  >
                                    Not Approved
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )
                          ) : (
                            <Badge className="border font-medium px-3 py-1 bg-yellow-50 text-yellow-700">
                              {tenureStatus}
                            </Badge>
                          );
                        }
                      })()}
                    </div>

                    {/* Verification Status */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Verification:</span>
                      {user.role === "ADMIN" ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className={cn("rounded-lg px-3 py-1.5", 
                                app.verificationStatus === "Verified" ? "border-green-300 text-green-600 hover:bg-green-50" : 
                                app.verificationStatus === "Not Verified" ? "border-red-300 text-red-600 hover:bg-red-50" : 
                                "border-orange-300 text-orange-600 hover:bg-orange-50")}
                            >
                              {app.verificationStatus} <ChevronDown className="ml-2 h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="rounded-lg shadow-lg border-0 bg-white/95 backdrop-blur-sm">
                            <DropdownMenuItem
                              onClick={() => handleVerificationStatusChange(app.applicationId, "Verified")}
                              className="cursor-pointer hover:bg-green-50 px-3 py-2 text-green-700"
                            >
                              Verified
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleVerificationStatusChange(app.applicationId, "Not Verified")}
                              className="cursor-pointer hover:bg-red-50 px-3 py-2 text-red-700"
                            >
                              Not Verified
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleVerificationStatusChange(app.applicationId, "Unknown")}
                              className="cursor-pointer hover:bg-gray-50 px-3 py-2 text-gray-700"
                            >
                              Unknown
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <span className="text-sm text-muted-foreground">{app.verificationStatus || "Select Status"}</span>
                      )}
                    </div>

                    {/* Tenure Days */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Tenure Days:</span>
                      <span className="text-sm">
                        {app.verificationStatus === "Not Verified" ? (
                          <Badge className="border font-medium px-3 py-1 bg-red-50 text-red-700">Not Eligible</Badge>
                        ) : (
                          `${app.tenure?.daysLeft ?? "N/A"} days`
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

        {/* Pagination */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 flex-shrink-0 bg-white border-t border-gray-200">
          <div className="text-sm text-muted-foreground text-center sm:text-left">
            Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
            {Math.min(pagination.page * pagination.limit, pagination.totalResults)} of{" "}
            {pagination.totalResults} candidates
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1 || isLoading}
              className="rounded-lg border-gray-200 hover:bg-orange-50"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            
            {/* Mobile: Show only current page and ellipsis */}
            <div className="hidden sm:flex items-center gap-2">
            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
              .filter(
                (pageNum) =>
                  pageNum === 1 ||
                  pageNum === pagination.totalPages ||
                  (pageNum >= pagination.page - 2 && pageNum <= pagination.page + 2)
              )
              .map((pageNum) => (
                <Button
                  key={pageNum}
                  variant={pagination.page === pageNum ? "default" : "outline"}
                  size="sm"
                  onClick={() => handlePageChange(pageNum)}
                  disabled={isLoading}
                  className={
                    pagination.page === pageNum
                      ? "bg-[#EC6E51] text-white hover:bg-orange-700"
                      : "border-gray-200 hover:bg-orange-50"
                  }
                >
                  {pageNum}
                </Button>
              ))}
            {pagination.totalPages > 5 && pagination.page < pagination.totalPages - 2 && (
              <span className="text-sm text-muted-foreground">...</span>
            )}
            </div>
            
            {/* Mobile: Show page number */}
            <div className="sm:hidden">
              <span className="text-sm text-gray-600 px-2">
                {pagination.page} / {pagination.totalPages}
              </span>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages || isLoading}
              className="rounded-lg border-gray-200 hover:bg-orange-50"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

      <Dialog open={showTenureApprovalDialog} onOpenChange={setShowTenureApprovalDialog}>
        <DialogContent className="max-h-[90vh] overflow-auto max-w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Tenure Approval</DialogTitle>
            <DialogDescription>
              Are you sure you want to approve the tenure for this candidate? Please provide payout details below.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Employee ID Assigned by Company
              </label>
              <Input
                value={payoutDetails.employeeIdAssignedByCompany || ""}
                onChange={(e) =>
                  setPayoutDetails((prev) => ({
                    ...prev,
                    employeeIdAssignedByCompany: e.target.value,
                  }))
                }
                className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
                placeholder="Enter Employee ID"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Final Position Name
              </label>
              <Input
                value={payoutDetails.finalPositionName || ""}
                onChange={(e) =>
                  setPayoutDetails((prev) => ({
                    ...prev,
                    finalPositionName: e.target.value,
                  }))
                }
                className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
                placeholder="Enter Position Name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Final Salary
              </label>
              <Input
                type="number"
                value={payoutDetails.finalSalary ?? ""}
                onChange={(e) =>
                  setPayoutDetails((prev) => ({
                    ...prev,
                    finalSalary: e.target.value ? Number(e.target.value) : undefined,
                  }))
                }
                className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
                placeholder="Enter Salary"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Commission Receivable from Company
              </label>
              <Input
                type="number"
                value={payoutDetails.commissionReceivableFromCompany ?? ""}
                onChange={(e) =>
                  setPayoutDetails((prev) => ({
                    ...prev,
                    commissionReceivableFromCompany: e.target.value ? Number(e.target.value) : undefined,
                  }))
                }
                className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
                placeholder="Enter Commission Receivable"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Commission Payable to Sourcer
              </label>
              <Input
                type="number"
                value={payoutDetails.commissionPayableToSourcer ?? ""}
                onChange={(e) =>
                  setPayoutDetails((prev) => ({
                    ...prev,
                    commissionPayableToSourcer: e.target.value ? Number(e.target.value) : undefined,
                  }))
                }
                className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
                placeholder="Enter Commission Payable"
                min="0"
              />
            </div>
            {/* <div>
              <label className="block text-sm font-medium text-gray-700">
                Commission Claimed
              </label>
              <Select
                value={payoutDetails.isSourcerCommissionClaimed ? "true" : "false"}
                onValueChange={(value) =>
                  setPayoutDetails((prev) => ({
                    ...prev,
                    isSourcerCommissionClaimed: value === "true",
                  }))
                }
              >
                <SelectTrigger className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500">
                  <SelectValue placeholder="Select Commission Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Claimed</SelectItem>
                  <SelectItem value="false">Not Claimed</SelectItem>
                </SelectContent>
              </Select>
            </div> */}
            {/* <div>
              <label className="block text-sm font-medium text-gray-700">
                Claimed By
              </label>
              <div className="w-full h-10 rounded-lg border-gray-200 bg-gray-100 flex items-center px-3 text-sm text-gray-700">
                {applications.find((app) => app.applicationId === selectedApplicationForTenure)?.ledBy?.username || "N/A"}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Claim Date
              </label>
              <Input
                type="date"
                value={payoutDetails.claimDate ? new Date(payoutDetails.claimDate).toISOString().split("T")[0] : ""}
                onChange={(e) =>
                  setPayoutDetails((prev) => ({
                    ...prev,
                    claimDate: e.target.value ? new Date(e.target.value) : undefined,
                  }))
                }
                className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
              />
            </div> */}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowTenureApprovalDialog(false);
                setSelectedApplicationForTenure(null);
                setPayoutDetails({
                  employeeIdAssignedByCompany: "",
                  finalPositionName: "",
                  finalSalary: undefined,
                  commissionReceivableFromCompany: undefined,
                  commissionPayableToSourcer: undefined,
                  claimedByUserId: "",
                  claimDate: undefined,
                });
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApproveTenure}
              disabled={
                !payoutDetails.commissionPayableToSourcer
              }
            >
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showNotApprovedDialog}
        onOpenChange={(open) => {
          setShowNotApprovedDialog(open);
          if (!open) {
            setSelectedApplicationForNotApproved(null);
            setLastWorkingDay("");
          }
        }}
      >
        <DialogContent className="max-w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Update Last Working Day</DialogTitle>
            <DialogDescription>
              Tenure status is being updated to Not Approved. You can submit with a Last Working Day or skip and continue without it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Last Working Day</label>
            <Input
              type="date"
              value={lastWorkingDay}
              onChange={(e) => setLastWorkingDay(e.target.value)}
              className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => handleNotApprovedStatusUpdate({ skip: true })}>
              Skip
            </Button>
            <Button onClick={() => handleNotApprovedStatusUpdate({ skip: false })} disabled={!lastWorkingDay}>
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JoinedCandidates;